import React from 'react';
import { Text } from '../text';
import { Button } from '../button';
import { ArrowUpTrayIcon } from '@heroicons/react/20/solid';
import { dueLabel } from '../../utils/convenioCompliance';
import { formatNumeroFianza } from '../../utils/calculations';

// Shared renderer so an emitted fianza and a requisito state their share of
// the contrato the same way.
const PctChip = ({ info }) => {
  if (!info) return null;
  return (
    <span
      className="px-2 py-0.5 text-xs font-semibold rounded-full bg-azul-surelia/10 text-azul-surelia tabular-nums"
      title={info.derived
        ? 'Calculado sobre el monto del contrato; el requisito no declaró un porcentaje.'
        : 'Porcentaje del monto del contrato que ampara esta fianza.'}
    >
      {info.pct}% del contrato{info.derived ? ' (calc.)' : ''}
    </span>
  );
};

// The drill-down for one contrato: every fianza emitida plus every requerida
// still por emitir. Shared by the Contratos, Proveedores and Proyectos tabs so
// every expansion shows the same rows — the count in the collapsed row and the
// list under it come from the same rollup.
//
// mode='client' is the beneficiario's view in the portal: it drops prima, pago
// status and endosos, which are the fiado's commercial data and must not cross
// to the other side of the convenio.
//
// mode='fiado' is the fiado's own portal (Empresa page): pago status IS the
// fiado's own data (the portal Fianzas view always showed it), so it stays;
// prima and endosos remain broker-only.

const formatMonto = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const STATUS_BADGE = {
  vigente: 'bg-green-100 text-green-800',
  vencida: 'bg-red-100 text-red-800',
  pendiente: 'bg-gray-100 text-gray-800',
  cancelada: 'bg-zinc-200 text-zinc-700',
  anulada: 'bg-zinc-200 text-zinc-700',
  sustituida: 'bg-zinc-200 text-zinc-700',
};

const PAGO_BADGE = {
  pagada: 'bg-green-100 text-green-800',
  parcial: 'bg-amber-100 text-amber-800',
  pendiente: 'bg-orange-100 text-orange-800',
};

const toneFor = (fr) => {
  if (fr.isFutura) {
    return { dot: 'bg-zinc-300', badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' };
  }
  if (fr.isOverdue) {
    return { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' };
  }
  return { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
};

const ContratoFianzasPanel = ({ stats, contratoId, onFianzaClick, onSubir, canUpload = true, mode = 'broker', stickyOffset = 0 }) => {
  const isBroker = mode === 'broker';
  const showPago = isBroker || mode === 'fiado';
  return (
  // Sticky so the drill-down stays readable however far the table is scrolled
  // sideways; stickyOffset parks it just clear of the frozen columns.
  //
  // A sticky box cannot be pushed past its containing block, so at the very end
  // of the scroll the panel gets shoved leftward, back over the frozen strip.
  // That is FINE — and deliberate — as long as the expansion row carries the
  // opaque z-10 spacer cell over the frozen columns (see the frozen tables'
  // expansion rows): the panel is z-auto, so it slides UNDER the spacer instead
  // of crossing the freeze divider. Drop the spacer and it crosses.
  <div className="sticky max-w-4xl" style={{ left: stickyOffset }}>
    {stats.rows.length > 0 ? (
      <div className="space-y-1.5">
        {stats.rows.map(({ fianza: f, status, prima, pagoEstado, pagadas, facturasTotal, endososPendientes }) => (
          <button
            key={f.id}
            onClick={(e) => {
              e.stopPropagation();
              onFianzaClick && onFianzaClick(contratoId, f);
            }}
            className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-white rounded-lg border border-azul-surelia/15 px-3 py-2 hover:border-azul-surelia/40 hover:shadow-sm transition-all duration-200"
          >
            <span className="text-sm font-mono text-zinc-700 min-w-[120px]">
              {formatNumeroFianza(f, 'Sin número')}
            </span>
            <span className="text-xs text-zinc-500 min-w-[100px] truncate">
              {f.tipoFianza || f.tipo || 'Sin tipo'}
            </span>
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_BADGE[status.code] || 'bg-gray-100 text-gray-800'}`}>
              {status.label}
            </span>
            {f.proveedorMismatch && (
              <span
                title={`Fiado en XML: ${f.proveedorMismatch.fiadoXmlNombre}. Prevalece el proveedor del contrato.`}
                className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800"
              >
                Fiado no coincide
              </span>
            )}
            <PctChip info={f.porcentajeInfo} />
            {showPago && (
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${PAGO_BADGE[pagoEstado]}`}>
                {pagoEstado === 'pagada' ? 'Pagada' : pagoEstado === 'parcial' ? `Parcial ${pagadas}/${facturasTotal}` : 'Pendiente'}
              </span>
            )}
            <span className="text-xs text-zinc-600 tabular-nums">
              <span className="text-zinc-400">Afianzado</span>{' '}
              {formatMonto(Number(f.montoAfianzado) || 0)}
              {isBroker && (
                <>
                  <span className="text-zinc-400 mx-1">·</span>
                  Prima {formatMonto(prima)}
                </>
              )}
            </span>
            {isBroker && endososPendientes > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-amber-600">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                {endososPendientes === 1 ? 'Endoso pendiente' : `${endososPendientes} endosos pendientes`}
              </span>
            )}
          </button>
        ))}
      </div>
    ) : (
      <Text className="text-sm text-zinc-500">Sin fianzas emitidas</Text>
    )}

    {stats.pendientes.length > 0 && (
      <div className={`space-y-1.5 ${stats.rows.length > 0 ? 'mt-1.5' : 'mt-2'}`}>
        {stats.pendientes.map((fr) => {
          const tone = toneFor(fr);
          return (
            <div
              key={fr._key}
              className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-white rounded-lg border border-dashed border-zinc-300 px-3 py-2"
            >
              <span className="flex items-center gap-2 min-w-[120px]">
                <span className={`w-2 h-2 rounded-full shrink-0 ${tone.dot}`} />
                <span className="text-sm font-medium text-zinc-700">{fr.tipo || 'Fianza'}</span>
              </span>
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>
                {tone.label}
              </span>
              <PctChip info={Number(fr.porcentaje) > 0 ? { pct: Number(fr.porcentaje), derived: false } : null} />
              <span className={`text-xs ${tone.due}`}>{dueLabel(fr)}</span>
              <span className="text-xs text-zinc-600 tabular-nums">
                <span className="text-zinc-400">Por afianzar</span> {formatMonto(fr.monto || 0)}
              </span>
              {canUpload && onSubir && (
                <Button
                  size="xs"
                  color="surelia"
                  outline
                  className="ml-auto"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSubir({
                      type: 'subir_fianza',
                      contratoId,
                      frKey: fr._key,
                      tipo: fr.tipo,
                      monto: fr.monto,
                    });
                  }}
                >
                  <ArrowUpTrayIcon className="h-3 w-3" />
                  Subir
                </Button>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
};

export default ContratoFianzasPanel;
